import {
  Button,
  Stepper,
  Step,
  StepLabel,
  WithStyles,
  withStyles,
  StepConnector as MiStepConnector,
} from '@material-ui/core';
import { equals, isNil } from 'ramda';
import { useState, useEffect, Fragment } from 'react';
import { useSelector } from 'react-redux';

import { InformPromptDialog, promptContents } from '../General';

import { ConnectSpeaker } from './ConnectSpeaker';
import { DefaultBusinessUnit } from './DefaultBusinessUnit';
import { styles } from './FirstLoginStepper.styles';
import { LicenseAgreement } from './LicenseAgreement/LicenseAgreement';
import { PasswordReset } from './PasswordReset';

import { selectors, boundActions } from '~store';
import { Dialog, DialogTitle, Divider } from '~ui-kit';

export const steps = ['Agreement', 'Change password', 'Add Business Unit', 'Connect Speaker'];

const getStepContent = (step: number) => {
  switch (step) {
    case 0: {
      return <LicenseAgreement />;
    }

    case 1: {
      return (
        <PasswordReset>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              boundActions.auth.logoutInit();
            }}
          >
            Logout
          </Button>
          <Button type="submit" variant="contained" color="primary">
            Next
          </Button>
        </PasswordReset>
      );
    }

    case 2: {
      return <DefaultBusinessUnit />;
    }

    case 3: {
      return <ConnectSpeaker />;
    }
  }
};

type StepConnectorProps = WithStyles<typeof styles>;
const StepConnector = withStyles(styles)(({ classes }: StepConnectorProps) => (
  <MiStepConnector classes={{ line: classes.line }} />
));

type FirstLoginStepperProps = WithStyles<typeof styles>;

export const FirstLoginStepper = withStyles(styles)(({ classes }: FirstLoginStepperProps) => {
  const [activeStep, setActiveStep] = useState(-1);
  const isAuthorized = useSelector(selectors.auth.isAuthorized, equals);
  const { isAgreed: isLicenseAgreement, isDbDumpSyncActive: isDbDumpOn } =
  useSelector(selectors.app.getAppInfo, equals);
  const shouldUpdatePassword = useSelector(selectors.auth.shouldUpdatePassword, equals);
  const isOnlyOrphanBusinessUnit = useSelector(selectors.businessUnits.isOnlyOrphanBusinessUnit, equals);
  const showULWarning = useSelector(selectors.app.showULWarning, equals);
  const isPrimary = useSelector(selectors.app.isPrimary, equals);
  const [showDBDumpWarning, setShowDBDumpWarning] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);

  const appDialog = useSelector(selectors.app.getAppDialogs, equals);
  const areBusinessUnits = useSelector(selectors.businessUnits.areBusinessUnitsIds, equals);
  const hasShowULWarning = showULWarning ? true : Boolean(shouldUpdatePassword);
  const isOpenDialog =
    appDialog.firstLoginStepper &&
    !appDialog.uploadMigratedDB &&
    !appDialog.selectMigratedBU &&
    areBusinessUnits &&
    hasShowULWarning;

  useEffect(() => {
    if (isAuthorized) {
      boundActions.businessUnits.fetchBusinessUnitsIdsInit({ includeOrphan: true });
    }
  }, [isAuthorized]);

  useEffect(() => {
    switch (true) {
      case !isLicenseAgreement: {
        setActiveStep(0);
        break;
      }

      case shouldUpdatePassword: {
        setActiveStep(1);
        break;
      }

      case isOnlyOrphanBusinessUnit: {
        setActiveStep(2);
        break;
      }

      default:
        if (showULWarning) {
          setActiveStep(3);
        } else {
          boundActions.app.setAppDialog({ dialog: 'firstLoginStepper', value: false });
        }

        if (!isNil(isDbDumpOn) && !isNil(isPrimary)) {
          if (isDbDumpOn && !isPrimary) {
            setShowDBDumpWarning(!isPrimary);
            setOpenWarning(!openWarning);
          }
        }
        break;
    }
  }, [isOpenDialog, shouldUpdatePassword, isOnlyOrphanBusinessUnit, isLicenseAgreement,
    isPrimary, isDbDumpOn]);

  return (
    <Fragment>
      <InformPromptDialog
        open={showDBDumpWarning && openWarning}
        onConfirm={() => setShowDBDumpWarning(false)}
      >
        {promptContents.dbDumpWarning}
      </InformPromptDialog>

      <Dialog open={isOpenDialog} fullWidth maxWidth="sm">
        <DialogTitle classes={{ title: classes.title }} title="To start working with INCC:" />

        <Stepper
          classes={{ horizontal: classes.horizontal }}
          activeStep={activeStep}
          alternativeLabel
          connector={<StepConnector />}
        >
          {steps.map(step => (
            <Step key={step} classes={{ completed: classes.completedStep }}>
              <StepLabel classes={{ iconContainer: classes.icon, alternativeLabel: classes.label }}>{step}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Divider classes={{ root: classes.divider }} />

        <div>{getStepContent(activeStep)}</div>
      </Dialog>
    </Fragment>
  );
});
